import { useState } from 'react';
import { Icon } from '../../../components/icons';
import { Button, Card, Field, Input, cn } from '../../../components/ui';
import { CopyButton, ToolPageLayout, type ToolPageContent } from './ToolPageLayout';

type KeyBits = 1024 | 2048;

interface GeneratedKey {
  publicTxt: string;
  publicTxtSplit: string;
  privatePem: string;
  bits: KeyBits;
}

/**
 * Free DKIM keypair generator. Runs ENTIRELY in the browser via SubtleCrypto.
 *
 * - 2048-bit RSA is the default (Google + Microsoft both reject 1024 in 2024+).
 * - Public key exports as SPKI then base64-encoded — same format opendkim and
 *   most DNS panels accept verbatim.
 * - Private key exports as PKCS#8 PEM, ready to drop into an outbound mail
 *   server config (Postfix's opendkim KeyFile, Mailgun's "private key" field).
 *
 * The private key NEVER leaves the device — no network call, no logging. The
 * page heading says so explicitly because users (rightly) get nervous about
 * pasting private-key generation into a web form.
 */
export function DkimGeneratorPage() {
  const [domain, setDomain] = useState('');
  const [selector, setSelector] = useState('mail');
  const [bits, setBits] = useState<KeyBits>(2048);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generated, setGenerated] = useState<GeneratedKey | null>(null);

  async function onGenerate() {
    setBusy(true);
    setError(null);
    try {
      const key = await generateDkimKeypair(bits);
      setGenerated(key);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const hostname =
    selector && domain
      ? `${selector}._domainkey.${domain.replace(/^_dmarc\./, '').replace(/^@/, '')}`
      : `${selector || 'selector'}._domainkey.yourdomain.com`;

  const content: ToolPageContent = {
    slug: 'dkim-generator',
    title: 'DKIM key generator',
    tagline: 'Generate a DKIM keypair in your browser — private key never leaves your device.',
    eyebrow: 'Free tool · No signup · 100% in-browser',
    description:
      'Free DKIM generator. Create a 2048-bit RSA keypair in your browser — TXT record for DNS, private key for your mail server. Never leaves your device.',
    icon: <Icon.Key />,
    accent: 'allow',
    intro:
      'Pick a selector + key size, hit Generate. The keypair is built locally via WebCrypto — the private key never touches a network. Paste the public-key TXT record into your DNS and the PEM into your mail server.',
    body: (
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1.4fr]">
        <Card className="p-6">
          <h2 className="text-sm font-semibold">Configuration</h2>
          <div className="mt-5 grid gap-4">
            <Field label="Your domain">
              <Input
                value={domain}
                onChange={(e) => setDomain(e.target.value.toLowerCase().trim())}
                placeholder="example.com"
                maxLength={253}
                autoFocus
              />
            </Field>

            <Field label="Selector" hint='Any short string — "mail", "google", "k1". The TXT record lives at <selector>._domainkey.<domain>.'>
              <Input
                value={selector}
                onChange={(e) => setSelector(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                placeholder="mail"
                maxLength={63}
              />
            </Field>

            <Field label="Key size" hint="2048 is the modern standard. 1024 only for legacy systems that can't handle the longer TXT record.">
              <div className="inline-flex rounded-md border border-border bg-bg p-1 text-[12.5px]">
                {([1024, 2048] as KeyBits[]).map((b) => (
                  <button
                    key={b}
                    type="button"
                    onClick={() => setBits(b)}
                    className={cn(
                      'rounded px-3 py-1.5 font-mono transition',
                      bits === b ? 'bg-accent text-[#04141d]' : 'text-muted hover:bg-hover hover:text-txt',
                    )}
                  >
                    {b}
                  </button>
                ))}
              </div>
            </Field>

            <Button variant="primary" onClick={onGenerate} disabled={busy}>
              {busy ? 'Generating…' : 'Generate keypair'}
            </Button>
            {error && <p className="text-[13px] text-review">{error}</p>}

            <p className="mt-2 rounded-md border border-allow/30 bg-allow/10 px-3 py-2 text-[12.5px] text-allow">
              Generation runs in your browser via WebCrypto. No network call is made; the private key never leaves your device.
            </p>
          </div>
        </Card>

        <Card className="p-6">
          {!generated && (
            <div className="flex h-full min-h-[280px] items-center justify-center text-center">
              <div>
                <Icon.Key />
                <p className="mt-3 text-[13px] text-muted">Generate a keypair to see the TXT record + private key.</p>
              </div>
            </div>
          )}
          {generated && (
            <div className="grid gap-5">
              <div>
                <div className="flex items-baseline justify-between">
                  <h3 className="text-sm font-semibold">Public key — DNS TXT record</h3>
                  <CopyButton text={generated.publicTxt} />
                </div>
                <div className="mt-3 font-mono text-[10.5px] uppercase tracking-wider text-faint">Hostname / Name</div>
                <pre className="mt-1 overflow-auto whitespace-pre-wrap break-all rounded-md border border-bordersoft bg-panel2 p-3 font-mono text-[12px] text-allow">{hostname}</pre>
                <div className="mt-3 font-mono text-[10.5px] uppercase tracking-wider text-faint">TXT value (single string)</div>
                <pre className="mt-1 overflow-auto rounded-md border border-bordersoft bg-panel2 p-3 font-mono text-[11px] text-allow whitespace-pre-wrap break-all">
                  {generated.publicTxt}
                </pre>
                {generated.publicTxt.length > 255 && (
                  <>
                    <div className="mt-3 font-mono text-[10.5px] uppercase tracking-wider text-faint">
                      TXT value (split into 255-char strings — for older DNS providers)
                    </div>
                    <pre className="mt-1 overflow-auto rounded-md border border-bordersoft bg-panel2 p-3 font-mono text-[11px] text-allow whitespace-pre-wrap break-all">
                      {generated.publicTxtSplit}
                    </pre>
                  </>
                )}
              </div>

              <div>
                <div className="flex items-baseline justify-between">
                  <h3 className="text-sm font-semibold">Private key — for your mail server</h3>
                  <CopyButton text={generated.privatePem} />
                </div>
                <pre className="mt-3 max-h-72 overflow-auto whitespace-pre-wrap break-all rounded-md border border-bordersoft bg-panel2 p-3 font-mono text-[10.5px] text-review">
                  {generated.privatePem}
                </pre>
                <p className="mt-2 text-[12px] text-review">
                  Treat this like a password. Anyone with this file can sign mail as if it were from your domain.
                </p>
              </div>
            </div>
          )}
        </Card>
      </div>
    ),
    educational: {
      title: 'What this generator produces',
      paragraphs: [
        'The keypair is generated in your browser and never leaves it. You publish the public half as a DNS TXT record under a selector you choose; your mail server signs with the private half.',
        '2048-bit is the default because Gmail and Outlook both treat 1024-bit as weak. A 2048-bit public key does not fit in one 255-byte DNS string, so the generator shows both the single-string and split-string forms — paste whichever your DNS provider accepts.',
      ],
    },
    faq: [
      { q: 'Where is the private key generated?', a: 'In your browser via the WebCrypto API. No data is sent over the network — the keypair never leaves your device unless you copy it yourself.' },
      { q: 'What\'s a DKIM selector?', a: 'A short label that disambiguates multiple DKIM keys on the same domain. The TXT record lives at <selector>._domainkey.<your-domain>. Common selectors: default, mail, google, k1, s1.' },
      { q: 'Why 2048 over 1024?', a: 'Gmail + Outlook both downrank 1024-bit keys and may eventually reject them. 1024 is only useful for legacy mail systems that can\'t process the longer TXT record.' },
      { q: 'My DNS provider says the TXT value is too long. What do I do?', a: 'Use the split format the tool shows — most providers accept the same record broken into 255-character strings concatenated together.' },
      { q: 'Where do I paste the private key?', a: 'Into your outbound mail server config — opendkim (KeyFile), Postfix milter, Mailgun "private key" field, AWS SES verified identity, etc.' },
    ],
    relatedTools: [
      { slug: 'spf-generator', title: 'SPF record generator', tagline: 'Build a valid SPF record from the senders you authorise.' },
      { slug: 'dmarc-generator', title: 'DMARC record generator', tagline: 'Build a valid DMARC TXT record with the right policy + reports.' },
      { slug: 'dkim-checker', title: 'DKIM record checker', tagline: 'Verify your published DKIM record looks right.' },
    ],
    ctaTitle: 'Watch DKIM drift across every domain',
    ctaBody:
      "SieveGuard's Domain Health add-on probes 14 common DKIM selectors on every domain you ingest and alerts when keys disappear, get revoked, or shrink below 2048 bits. Free during your trial.",
  };

  return <ToolPageLayout content={content} />;
}

async function generateDkimKeypair(bits: KeyBits): Promise<GeneratedKey> {
  if (typeof crypto === 'undefined' || !crypto.subtle) {
    throw new Error("This browser doesn't support WebCrypto. Try a recent Chrome / Firefox / Safari.");
  }
  const keypair = await crypto.subtle.generateKey(
    {
      name: 'RSASSA-PKCS1-v1_5',
      modulusLength: bits,
      publicExponent: new Uint8Array([0x01, 0x00, 0x01]),
      hash: 'SHA-256',
    },
    true,
    ['sign', 'verify'],
  );
  const spki = await crypto.subtle.exportKey('spki', keypair.publicKey);
  const pkcs8 = await crypto.subtle.exportKey('pkcs8', keypair.privateKey);
  const spkiBase64 = arrayBufferToBase64(spki);
  const publicTxt = `v=DKIM1; k=rsa; p=${spkiBase64}`;
  const publicTxtSplit = splitIntoQuotedStrings(publicTxt, 255);
  const privatePem = arrayBufferToPem(pkcs8, 'PRIVATE KEY');
  return { publicTxt, publicTxtSplit, privatePem, bits };
}

function arrayBufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  return btoa(bin);
}

function arrayBufferToPem(buf: ArrayBuffer, label: string): string {
  const b64 = arrayBufferToBase64(buf);
  const lines = b64.match(/.{1,64}/g) ?? [];
  return `-----BEGIN ${label}-----\n${lines.join('\n')}\n-----END ${label}-----`;
}

/** Many DNS panels reject TXT values > 255 chars unless split into multiple
 *  quoted strings concatenated by whitespace. */
function splitIntoQuotedStrings(value: string, maxLen: number): string {
  const chunks: string[] = [];
  for (let i = 0; i < value.length; i += maxLen) {
    chunks.push(`"${value.slice(i, i + maxLen)}"`);
  }
  return chunks.join(' ');
}

// Default export required by React Router framework-mode route modules.
export default DkimGeneratorPage;
